/**
 * AddressCorrectionReviewSection - Main container for address correction review UI
 *
 * SESSION 197: Address Correction Frontend Admin UI
 *
 * Provides complete address correction review workflow with:
 * - Status filter
 * - Paginated correction list
 * - Approve/reject actions
 */
import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Stack, Title, Group, Text, Badge, Box } from '@mantine/core';
import { IconFileCheck } from '@tabler/icons-react';
import { AddressCorrectionFilters } from './AddressCorrectionFilters';
import { AddressCorrectionList } from './AddressCorrectionList';
import {
  useAddressCorrections,
  useApproveAddressCorrection,
  useRejectAddressCorrection
} from '@/application/queries/addressCorrectionQueries';
import type { AddressCorrectionDto, AddressCorrectionStatus } from '@/domain/entities/AddressCorrection';
import type { AddressCorrectionReviewSectionProps } from './AddressCorrectionReviewSection.types';

/**
 * AddressCorrectionReviewSection Component
 *
 * Main container for address correction review workflow
 * Manages state for filters and pagination
 */
export const AddressCorrectionReviewSection: React.FC<AddressCorrectionReviewSectionProps> = ({
  className
}): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filters from URL query params
  const status = (searchParams.get('status') || 'Pending') as AddressCorrectionStatus | 'All';
  const page = searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1;
  const pageSize = 25;

  // Helper to update query params
  const updateQueryParam = useCallback((key: string, value: string | number | null): void => {
    const newParams = new URLSearchParams(searchParams);
    if (value === null || value === '') {
      newParams.delete(key);
    } else {
      newParams.set(key, String(value));
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Filter change handlers
  const handleStatusChange = useCallback((value: AddressCorrectionStatus | 'All'): void => {
    updateQueryParam('status', value === 'All' ? null : value);
    updateQueryParam('page', '1'); // Reset to page 1 when filter changes
  }, [updateQueryParam]);

  const handlePageChange = useCallback((value: number): void => {
    updateQueryParam('page', String(value));
  }, [updateQueryParam]);

  // Clear all filters
  const handleClearFilters = useCallback((): void => {
    setSearchParams(new URLSearchParams({
      status: 'Pending',
      page: '1'
    }));
  }, [setSearchParams]);

  // Query for displayed corrections
  const { data, isLoading, error } = useAddressCorrections({
    page,
    pageSize,
    status: status === 'All' ? undefined : status
  });

  // Separate query for pending count (always visible)
  const { data: pendingData } = useAddressCorrections({
    page: 1,
    pageSize: 1, // Minimize data transfer - we only need the count
    status: 'Pending'
  });

  // Mutations
  const approveMutation = useApproveAddressCorrection();
  const rejectMutation = useRejectAddressCorrection();

  const actionsDisabled = approveMutation.isPending || rejectMutation.isPending;

  // Approve handler
  const handleApprove = useCallback(async (
    correction: AddressCorrectionDto,
    adminNotes?: string
  ): Promise<void> => {
    try {
      await approveMutation.mutateAsync({
        id: correction.requestId,
        command: adminNotes ? { adminNotes } : undefined
      });
    } catch (error) {
      console.error('Failed to approve address correction:', error);
    }
  }, [approveMutation]);

  // Reject handler
  const handleReject = useCallback(async (
    correction: AddressCorrectionDto,
    rejectionReason: string,
    adminNotes?: string
  ): Promise<void> => {
    try {
      await rejectMutation.mutateAsync({
        id: correction.requestId,
        command: {
          rejectionReason,
          adminNotes
        }
      });
    } catch (error) {
      console.error('Failed to reject address correction:', error);
    }
  }, [rejectMutation]);

  return (
    <Box className={className} w="100%">
      <Stack gap="lg" w="100%">
        {/* Header */}
        <Group justify="space-between" wrap="wrap">
          <Stack gap="xs">
            <Title order={2}>
              <Group gap="xs">
                <IconFileCheck size={28} />
                <span>Address Corrections</span>
              </Group>
            </Title>
            <Text size="sm" c="dimmed">
              Review and approve address correction requests from business owners
            </Text>
          </Stack>

          {/* Stats */}
          {data && (
            <Group gap="md">
              {/* Always show pending count */}
              {pendingData && pendingData.totalCount > 0 && (
                <Badge size="lg" variant="filled" color="yellow">
                  {pendingData.totalCount} Pending
                </Badge>
              )}
              <Badge size="lg" variant="filled" color="blue">
                {data.totalCount} {status === 'All' ? 'Total' : status}
              </Badge>
            </Group>
          )}
        </Group>

        {/* Filters */}
        <AddressCorrectionFilters
          status={status}
          onStatusChange={handleStatusChange}
          onClearFilters={handleClearFilters}
        />

        {/* Correction List */}
        <AddressCorrectionList
          corrections={data?.requests || []}
          isLoading={isLoading}
          error={error}
          page={page}
          totalPages={data?.totalPages || 0}
          onPageChange={handlePageChange}
          onApprove={handleApprove}
          onReject={handleReject}
          actionsDisabled={actionsDisabled}
        />
      </Stack>
    </Box>
  );
};
